import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { mh, useMhStream } from "@/lib/mh";
import { onb, money } from "@/lib/onb";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow } from "@/components/mh/bits";
import { ModeSwitch, FilterChips, Legend, ViewSwitch } from "@/components/mh/SkyParts";
import { SkyView } from "@/components/mh/SkyView";
import { DeptView } from "@/components/mh/DeptView";
import { MapOverview } from "@/components/mh/MapOverview";
import { RolePanel } from "@/components/mh/RolePanel";
import { RoleSearch } from "@/components/mh/RoleSearch";
import { TailoredChip } from "@/components/mh/TailoredBrief";

function useSky() {
  const [deps, setDeps] = useState([]);
  const [agents, setAgents] = useState([]);
  const [onbSt, setOnbSt] = useState(null);
  const prevJob = useRef(null);
  const [settled, setSettled] = useState({});
  const loadAgents = useCallback(() => mh.agents().then((r) => setAgents(r.data)).catch(() => {}), []);
  const loadOnb = useCallback(() => onb.state().then((r) => setOnbSt({ interviews: r.data.interviews, job: r.data.job, profile: r.data.profile })).catch(() => {}), []);
  useEffect(() => { mh.departments().then((r) => setDeps(r.data)).catch(() => {}); loadAgents(); loadOnb(); }, [loadAgents, loadOnb]);
  useMhStream((ev) => {
    if (ev.type === "tailor.progress") {
      const was = prevJob.current?.id === ev.job.id ? prevJob.current.depts || {} : {};
      const fresh = Object.entries(ev.job.depts || {}).filter(([d, s]) => s === "done" && was[d] !== "done").map(([d]) => d);
      prevJob.current = ev.job;
      setOnbSt((s) => s && { ...s, job: ev.job });
      if (fresh.length) { loadAgents(); setSettled((x) => ({ ...x, ...Object.fromEntries(fresh.map((d) => [d, Date.now()])) })); }
    }
    if (ev.type === "onb.interview") loadOnb();
    if (ev.type === "tailor.role") loadAgents();
    if (ev.type === "agent.status") setAgents((as) => as.map((a) => (a.slug === ev.agent_slug ? { ...a, runtime: { ...(a.runtime || {}), status: ev.status } } : a)));
  });
  useEffect(() => { prevJob.current = onbSt?.job || null; }, [onbSt?.job?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const tailored = agents.some((a) => a.tailored);
  const iv = useMemo(() => Object.fromEntries((onbSt?.interviews?.departments || []).map((d) => [d.slug, d.status])), [onbSt]);
  const onbActive = !!onbSt?.job || Object.values(iv).some((s) => s !== "not_started");
  const needs = useCallback((dept) => (onbActive || tailored) && iv[dept] !== "done", [onbActive, tailored, iv]);
  return { deps, agents, onbSt, settled, tailored, iv, onbActive, needs };
}

export default function Constellation() {
  const nav = useNavigate();
  const loc = useLocation();
  const { dept: deptSlug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { current } = useAuth();
  const s = useSky();
  const { deps, agents, tailored } = s;
  const key = `mh-sky-mode-${current?.id}`;
  const [modePref, setModePref] = useState(() => localStorage.getItem(key));
  const mode = tailored ? modePref || "tailored" : "standard";
  const setMode = (m) => { localStorage.setItem(key, m); setModePref(m); };
  const view = sp.get("view") || localStorage.getItem("mh-map-view") || "sky";
  const setView = (v) => { localStorage.setItem("mh-map-view", v); setSp({ view: v }, { replace: true }); };
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [fly, setFly] = useState(null);
  const isT = mode === "tailored";
  const dept = deps.find((d) => d.slug === deptSlug);
  const fromSky = loc.state?.fromSky;
  const back = useCallback(() => (fromSky ? nav(-1) : nav("/map")), [fromSky, nav]);
  const closePanel = useCallback(() => setOpen(null), []);
  const onEnter = (slug, role) => nav(`/map/${slug}${role ? `?role=${role}` : ""}`, { state: { fromSky: true } });
  const pick = (a) => (!deptSlug && view === "sky" ? setFly({ dept: a.department, role: a.slug, t: Date.now() }) : nav(`/map/${a.department}?role=${a.slug}`, { state: loc.state, replace: !!deptSlug }));

  const live = agents.filter((a) => a.runtime?.status === "live").length;
  const active = agents.filter((a) => a.tailored?.enabled);
  const lo = active.reduce((x, a) => x + (a.tailored.value?.money_low || 0), 0), hi = active.reduce((x, a) => x + (a.tailored.value?.money_high || 0), 0);
  if (deptSlug && deps.length && !dept) return <Navigate to="/map" replace />;

  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="constellation-page">
      {deptSlug ? (
        dept && <DeptView key={dept.slug} d={dept} agents={agents} needsDept={s.needs(dept.slug)} onOpen={setOpen} back={back} panelOpen={!!open} profile={s.onbSt?.profile} search={<RoleSearch agents={agents} deps={deps} onPick={pick} />} />
      ) : <>
        <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-3 mh-rise">
          <div>
            <Eyebrow>{isT ? <span data-testid="sky-summary">{active.length} active of {agents.length} · {money(lo)}–{money(hi)} projected / month · {live} live</span> : `Constellation · ${live} of ${agents.length} live`}</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">{isT ? <>{current?.name}'s workforce, <em className="text-muted-foreground">in one sky.</em></> : <>The whole house, <em className="text-muted-foreground">in one sky.</em></>}</h1>
            {isT && <TailoredChip profile={s.onbSt?.profile} />}
          </div>
          <div className="flex flex-wrap items-center gap-2"><ViewSwitch view={view} setView={setView} /><ModeSwitch mode={mode} setMode={setMode} tailored={tailored} /></div>
        </header>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
            <RoleSearch agents={agents} deps={deps} onPick={pick} />
            {isT && view === "sky" && <FilterChips filter={filter} setFilter={setFilter} />}
          </div>
          {view === "sky" && <Legend tailored={isT} />}
        </div>
        {view === "map" ? <MapOverview deps={deps} agents={agents} needs={s.needs} />
          : <SkyView s={s} isT={isT} mode={mode} filter={filter} onOpenRole={setOpen} onEnter={onEnter} fly={fly} />}
      </>}
      {open && <RolePanel slug={open} agents={agents} onClose={closePanel} onSwitch={setOpen} />}
    </div>
  );
}
